import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../../utils/api';
import { fetchAvatarsBulk } from '../../utils/avatars';
import { flairNameProps } from '../../utils/flair';
import SteamAvatar from '../common/SteamAvatar';
import FlairEmblem from '../shop/FlairEmblem';
import './Leaderboard.css';

/**
 * PredictionLeaderboardCard — poängtoppen för matchtips.
 *
 * Visar de bästa tipsarna (summa odds-viktade poäng) med träffsäkerhet och
 * antal rättade tips. Bara avgjorda matcher räknas — backend aggregerar
 * enbart tips med satt `points_awarded`.
 *
 * Tipsligan är säsongstävlingen: default visas aktiva säsongen så nya
 * spelare kan tävla, med en all-time-flik för skrytvärdet. Plånboken
 * (saldot) påverkas aldrig av säsongsskiften — bara den här tabellen.
 *
 * @param {{onSelectPlayer?: function}} props Klick-callback som navigerar
 *   till spelarprofilen (steamid64), samma mönster som övriga leaderboards.
 * @returns {JSX.Element} Leaderboard-kortet.
 */
const PredictionLeaderboardCard = ({ onSelectPlayer }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState('current');
  const [seasonName, setSeasonName] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`${API_URL}/api/predictions/leaderboard?season=${scope}`)
      .then((res) => res.json())
      .then(async (body) => {
        if (cancelled) return;
        const list = Array.isArray(body?.leaderboard) ? body.leaderboard.slice(0, 5) : [];
        await fetchAvatarsBulk(list.map((p) => p.steamid64).filter(Boolean));
        if (cancelled) return;
        setData(list);
        setSeasonName(body?.season?.name || null);
        setLoading(false);
      })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [scope]);

  return (
    <div className="leaderboard-card">
      <div className="leaderboard-header">
        <span className="leaderboard-icon">🔮</span>
        <h3>{t('predictions.leaderboardTitle')}</h3>
        <div className="leaderboard-scope-toggle">
          <button
            type="button"
            className={`leaderboard-scope-btn ${scope === 'current' ? 'active' : ''}`}
            onClick={() => setScope('current')}
            title={seasonName || t('predictions.scopeSeason')}
          >
            {t('predictions.scopeSeason')}
          </button>
          <button
            type="button"
            className={`leaderboard-scope-btn ${scope === 'all' ? 'active' : ''}`}
            onClick={() => setScope('all')}
          >
            {t('predictions.scopeAllTime')}
          </button>
        </div>
      </div>
      {loading ? (
        <div className="loading-skeleton">
          {[...Array(5)].map((_, i) => <div key={i} className="skeleton-row" />)}
        </div>
      ) : data.length === 0 ? (
        <div className="leaderboard-empty">{t('predictions.leaderboardEmpty')}</div>
      ) : (
        <div className="leaderboard-list">
          {data.map((entry, index) => (
            <div
              key={entry.player_id || index}
              className={`leaderboard-row ${entry.steamid64 ? 'clickable' : ''} ${index === 0 ? 'top-player' : ''}`}
              onClick={() => onSelectPlayer && entry.steamid64 && onSelectPlayer(entry.steamid64)}
            >
              <span className={`rank rank-${index + 1}`}>{index + 1}</span>
              {entry.steamid64 && (
                <SteamAvatar steamid64={entry.steamid64} name={entry.name} size="small" />
              )}
              <span className="leaderboard-identity">
                <span {...flairNameProps(entry.flair_name_color)}>
                  {entry.name}
                  {entry.flair_badge && ` ${entry.flair_badge}`}
                  <span className="prediction-lb-accuracy">
                    {Math.round(entry.accuracy * 100)}% · {entry.predictions} {t('predictions.votes')}
                  </span>
                </span>
                <FlairEmblem emblem={entry.flair_emblem} />
              </span>
              <span className="player-stat">{entry.points} p</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PredictionLeaderboardCard;
